export class AddressUtils {
  /**
   * Ajoute la propriété `display` (résultat de formatInline) à une adresse,
   * utilisée par les autocompletes configurés avec displayProperty="display".
   */
  static withDisplay<T extends { [key: string]: any } | null | undefined>(address: T): T {
    if (!address) return address;
    (address as any).display = AddressUtils.formatInline(address);
    return address;
  }

  /**
   * Version liste de withDisplay : applique l'enrichissement à chaque adresse du tableau.
   */
  static withDisplayMany<T extends { [key: string]: any }>(addresses: T[]): T[] {
    if (!addresses) return addresses;
    return addresses.map(address => AddressUtils.withDisplay(address));
  }

  /**
   * Formate une adresse en une seule ligne pour affichage.
   * Convention : street, additionalAddress, postalCode city, country
   * @param address Objet adresse contenant street, additionalAddress, postalCode, city, country
   * @param prefix Préfixe optionnel (ex: nom d'établissement) affiché avant l'adresse
   */
  static formatInline(address: any, prefix?: string): string {
    if (!address) return '';

    const parts: string[] = [];

    if (address.street) parts.push(address.street);
    if (address.additionalAddress) parts.push(address.additionalAddress);

    const postalCity = `${address.postalCode || ''} ${address.city || ''}`.trim();
    if (postalCity) parts.push(postalCity);

    if (address.country) parts.push(address.country);

    const formatted = parts.join(', ');
    return prefix ? `${prefix} - ${formatted}` : formatted;
  }

  /**
   * Formate une adresse avec adresse en premier, suivie des noms d'établissements.
   * Format : "{rue}, {cp} {ville} — {Établissement1}, {Établissement2}, ..."
   * Utilisé pour les dropdowns de sélection d'adresse de livraison.
   */
  static formatAddressFirst(address: any, establishmentLabels: string[]): string {
    if (!address) return '';

    const parts: string[] = [];
    if (address.street) parts.push(address.street);
    if (address.additionalAddress) parts.push(address.additionalAddress);

    const postalCity = `${address.postalCode || ''} ${address.city || ''}`.trim();
    if (postalCity) parts.push(postalCity);

    const formatted = parts.join(', ');
    const establishments = establishmentLabels.length > 0
      ? establishmentLabels.join(', ')
      : 'Établissement inconnu';

    return `${formatted} — ${establishments}`;
  }

  /**
   * Extrait tous les établissements uniques de tous les bâtiments d'une adresse.
   * Retourne un tableau d'objets { _id, label } dédupliqué par _id.
   */
  static extractEstablishments(address: any): Array<{ _id: string; label: string }> {
    const establishments: Array<{ _id: string; label: string }> = [];
    const seenIds = new Set<string>();

    if (address.buildingsInfo && Array.isArray(address.buildingsInfo)) {
      for (const building of address.buildingsInfo) {
        if (building.establishments && Array.isArray(building.establishments)) {
          for (const est of building.establishments) {
            if (est._id && !seenIds.has(est._id.toString())) {
              seenIds.add(est._id.toString());
              establishments.push({ _id: est._id.toString(), label: est.label || 'Établissement inconnu' });
            }
          }
        }
      }
    }

    return establishments;
  }
}
